/**
 * DefaultPathDirtyBadge — small amber pill + restore button shown when the
 * user has edited a default-path row (toggled active, weight or name) but
 * has not yet pushed the change to Voluum. Lets them undo their row-level
 * edits in one click without losing edits on other rows.
 */

import type { FC } from 'react';
import { RotateCcw } from 'lucide-react';

import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

export interface DefaultPathDirtyBadgeProps {
  changedFields: ReadonlyArray<string>;
  onRestore: () => void;
  className?: string;
}

function describeChanges(fields: ReadonlyArray<string>): string {
  if (fields.length === 0) return 'Unsaved edits on this path';
  const pretty = fields
    .map((f) => (f === 'active' ? 'on/off' : f))
    .join(', ');
  return `Unsaved edits on this path: ${pretty}`;
}

const DefaultPathDirtyBadge: FC<DefaultPathDirtyBadgeProps> = ({
  changedFields,
  onRestore,
  className,
}) => {
  const description = describeChanges(changedFields);
  return (
    <span
      className={cn('flex shrink-0 items-center gap-1', className)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Tooltip content={description} side="bottom">
        <span
          aria-label={description}
          className="rounded bg-warning/15 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning"
        >
          Edited
        </span>
      </Tooltip>
      <Tooltip content="Restore this path to Voluum's current value" side="bottom">
        <button
          type="button"
          onClick={onRestore}
          aria-label="Restore this path"
          className="flex h-4 w-4 items-center justify-center rounded text-warning hover:bg-warning/10"
        >
          <RotateCcw className="h-2.5 w-2.5" aria-hidden />
        </button>
      </Tooltip>
    </span>
  );
};

export default DefaultPathDirtyBadge;
